import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';

import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { isEmbeddedDocument } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import {
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@trello/nachos/onboarding-experimental';
import { useIsInboxBoardPassContextVals } from '@trello/personal-workspace';
import { useSharedStateSelector } from '@trello/shared-state';
import {
  useSplitScreenResize,
  useSplitScreenSharedState,
} from '@trello/split-screen';

import { TrelloBoard } from 'app/src/components/Board';
import { legacyBoardModelsSharedState } from 'app/src/components/Board/legacyBoardModelsSharedState';
import { LazyInboxContainer } from 'app/src/components/Inbox';
import { INBOX_ROOT_ID } from 'app/src/components/Inbox/Inbox.constants';
import { useInboxSocketSubscription } from 'app/src/components/Inbox/useInboxSocketSubscription';
import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta';
import { PPOnboardingModalContainer } from 'app/src/components/PersonalProductivityBeta/PPOnboardingModal/PPOnboardingModalContainer';
import {
  INBOX_SPOTLIGHT,
  usePersonalProductivitySpotlightTour,
} from 'app/src/components/PersonalProductivityOnboarding';
import { LazyPlanner } from 'app/src/components/Planner';
import {
  CollapsibleSplitScreenPanel,
  CollapsibleSplitScreenResizeDivider,
} from 'app/src/components/SplitScreenPanel';
import { PanelNavigation } from './PanelNavigation';
import { RovoIsland } from './RovoIsland';

import styles from './ResizableCollapsibleSplitScreenManager.less';

const SplitScreen: FunctionComponent = () => {
  const { board } = useSharedStateSelector(
    legacyBoardModelsSharedState,
    useCallback((state) => {
      return {
        board: state.board,
      };
    }, []),
  );

  const memberId = useMemberId();
  const boardId = board.model?.get('id') ?? null;
  const workspaceId = board.model?.get('idOrganization') ?? '';
  const isInboxBoard = useIsInboxBoardPassContextVals({
    boardId,
    memberId,
  });
  const screenManagerRef = useRef<HTMLDivElement>(null);

  const {
    ref,
    configuration,
    panels,
    areMultiplePanelsOpen,
    isBoardOpenBlocked,
    toggleBoard,
    toggleInbox,
    togglePlanner,
    resizeAllPanelsToDefault,
    updateResizedPanelWidth,
    defaultPanelWidths,
  } = useSplitScreenSharedState({
    isInboxBoard,
    isDragToCollapseBoardEnabled: true,
  });

  const { sendResizeAnalyticsEvent } = useSplitScreenResize();

  const [inboxWidth, setInboxWidth] = useState(panels.panelWidths.inbox.width);
  const [plannerWidth, setPlannerWidth] = useState(
    panels.panelWidths.planner.width,
  );
  const [boardWidth, setBoardWidth] = useState(panels.panelWidths.board.width);

  const boardRef = useRef<HTMLDivElement>(null);

  const [isInboxCollapsed, setIsInboxCollapsed] = useState(!panels.inbox);
  const [isPlannerCollapsed, setIsPlannerCollapsed] = useState(!panels.planner);
  const [isBoardCollapsed, setIsBoardCollapsed] = useState(!panels.board);

  const [enableTransitions, setEnableTransitions] = useState(true);

  useInboxSocketSubscription();

  const { renderActiveSpotlight } = usePersonalProductivitySpotlightTour({
    source: 'inbox',
    boardId,
  });

  const toggleCallback = useCallback(() => {
    setBoardWidth(panels.panelWidths.board.width);
  }, [panels.panelWidths.board.width]);

  useEffect(() => {
    if (panels.inbox) {
      setInboxWidth(panels.panelWidths.inbox.width);
    }
    if (panels.planner) {
      setPlannerWidth(panels.panelWidths.planner.width);
    }
    if (panels.board) {
      setBoardWidth(panels.panelWidths.board.width);
    }
  }, [panels.inbox, panels.planner, panels.board, panels.panelWidths]);

  const handleDoubleClick = useCallback(() => {
    resizeAllPanelsToDefault();

    if (inboxWidth !== defaultPanelWidths.inbox.width) {
      sendResizeAnalyticsEvent('inbox', defaultPanelWidths.inbox.width);
    }

    if (plannerWidth !== defaultPanelWidths.planner.width) {
      sendResizeAnalyticsEvent('planner', defaultPanelWidths.planner.width);
    }
    if (boardWidth !== defaultPanelWidths.board.width) {
      sendResizeAnalyticsEvent('board', defaultPanelWidths.board.width);
    }
    setInboxWidth(defaultPanelWidths.inbox.width);
    setPlannerWidth(defaultPanelWidths.planner.width);
    setBoardWidth(defaultPanelWidths.board.width);
  }, [
    boardWidth,
    defaultPanelWidths.board.width,
    defaultPanelWidths.inbox.width,
    defaultPanelWidths.planner.width,
    inboxWidth,
    plannerWidth,
    resizeAllPanelsToDefault,
    sendResizeAnalyticsEvent,
  ]);

  const maybeUpdateBoard = useCallback(() => {
    if (panels.board && boardRef.current) {
      setBoardWidth(boardRef.current.offsetWidth);
      updateResizedPanelWidth('board', boardRef.current.offsetWidth);
    }
  }, [panels.board, updateResizedPanelWidth]);

  const maybeUpdatePlanner = useCallback(
    (width: number) => {
      if (panels.board && panels.planner) {
        setPlannerWidth(width);
        updateResizedPanelWidth('planner', width);
      }
    },
    [panels.board, panels.planner, updateResizedPanelWidth],
  );

  const handleSetPanelWidth = useCallback(
    (panelName: 'board' | 'inbox' | 'planner', width: number) => {
      switch (panelName) {
        case 'inbox':
          setInboxWidth(width);
          setIsInboxCollapsed(width === 0);
          break;
        case 'planner':
          setPlannerWidth(width);
          setIsPlannerCollapsed(width === 0);
          break;
        case 'board':
          setBoardWidth(width);
          setIsBoardCollapsed(width === 0);
          break;
        default:
          break;
      }
    },
    [],
  );

  return (
    <div className={styles.manager} ref={ref}>
      <SpotlightManager blanketIsTinted={true}>
        <div
          className={cx({
            [styles.splitscreen]: true,
            [styles.tabs]: configuration === 'tabs',
            [styles.single]: !areMultiplePanelsOpen,
          })}
          ref={screenManagerRef}
        >
          <CollapsibleSplitScreenPanel
            panelName={'inbox'}
            isHidden={!panels.inbox}
            panelWidth={inboxWidth}
            enableTransitions={enableTransitions}
            isCollapsed={isInboxCollapsed && inboxWidth === 0}
            shouldGrow={!panels.planner && (isBoardCollapsed || !panels.board)}
          >
            <SpotlightTarget name={INBOX_SPOTLIGHT}>
              <div id={INBOX_ROOT_ID} className={styles.inboxContent}>
                {panels.inbox && <LazyInboxContainer />}
              </div>
            </SpotlightTarget>
            <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>
          </CollapsibleSplitScreenPanel>
          <CollapsibleSplitScreenResizeDivider
            leftPreviousWidth={panels.panelWidths.inbox.width}
            rightPreviousWidth={
              panels.planner
                ? panels.panelWidths.planner.width
                : panels.panelWidths.board.width
            }
            enableTransitionsCallback={setEnableTransitions}
            shouldHideDivider={
              !panels.inbox ||
              (!panels.planner && !panels.inbox) ||
              (!panels.planner && !panels.board)
            }
            leftPanelName={'inbox'}
            rightPanelName={panels.planner ? 'planner' : 'board'}
            handleSetWidthCallback={handleSetPanelWidth}
            handleDoubleClick={handleDoubleClick}
            maybeUpdateBoardCallback={maybeUpdateBoard}
            maybeUpdatePlannerCallback={maybeUpdatePlanner}
          />
          <CollapsibleSplitScreenPanel
            panelName={'planner'}
            isHidden={!panels.planner}
            panelWidth={plannerWidth}
            enableTransitions={enableTransitions}
            isCollapsed={isPlannerCollapsed && plannerWidth === 0}
            shouldGrow={isBoardCollapsed || !panels.board}
          >
            <div id="trello-planner-root" className={styles.content}>
              {panels.planner && (
                <LazyPlanner screenManagerRef={screenManagerRef} />
              )}
            </div>
          </CollapsibleSplitScreenPanel>
          <CollapsibleSplitScreenResizeDivider
            leftPreviousWidth={
              panels.planner
                ? panels.panelWidths.planner.width
                : panels.panelWidths.inbox.width
            }
            rightPreviousWidth={panels.panelWidths.board.width}
            enableTransitionsCallback={setEnableTransitions}
            shouldHideDivider={!panels.planner || !panels.board}
            leftPanelName={'planner'}
            rightPanelName={'board'}
            handleSetWidthCallback={handleSetPanelWidth}
            handleDoubleClick={handleDoubleClick}
            maybeUpdateBoardCallback={maybeUpdateBoard}
            maybeUpdatePlannerCallback={maybeUpdatePlanner}
          />
          <CollapsibleSplitScreenPanel
            panelName={'board'}
            isHidden={!panels.board}
            panelWidth={boardWidth}
            enableTransitions={enableTransitions}
            isCollapsed={isBoardCollapsed && boardWidth === 0}
            ref={boardRef}
          >
            <div id="trello-board-root" className={styles.content}>
              <TrelloBoard />
            </div>
          </CollapsibleSplitScreenPanel>
        </div>

        <PanelNavigation
          {...panels}
          configuration={configuration}
          toggleInbox={toggleInbox}
          togglePlanner={togglePlanner}
          toggleBoard={toggleBoard}
          isBoardOpenBlocked={isBoardOpenBlocked}
          toggleCallback={toggleCallback}
        />
        {configuration === 'panels' && <RovoIsland workspaceId={workspaceId} />}
      </SpotlightManager>
    </div>
  );
};

export const ResizableCollapsibleSplitScreenManager: FunctionComponent = () => {
  const { value: isPersonalProdEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const [isOnboardingOpen, setIsOnboardingOpen] = useState(true);

  const personalProductivityCohort = usePersonalProductivityCohortValue();

  const closeOnboarding = useCallback(() => {
    setIsOnboardingOpen(false);
  }, []);

  const showSplitScreenOnboarding = useMemo(
    () =>
      !isOneTimeMessageDismissed('split-screen-onboarding') &&
      !isOneTimeMessageDismissed('new-user-onboarding') &&
      isOnboardingOpen &&
      personalProductivityCohort !== 'ga',
    [isOneTimeMessageDismissed, isOnboardingOpen, personalProductivityCohort],
  );

  if (!isPersonalProdEnabled || isEmbeddedDocument() || !isMemberLoggedIn()) {
    return <TrelloBoard />;
  }

  if (showSplitScreenOnboarding) {
    return <PPOnboardingModalContainer onComplete={closeOnboarding} />;
  }

  return <SplitScreen />;
};
